import React, { useEffect, useState } from 'react';
import { InputNumber, Tooltip } from 'antd';
import { usePieceDispatch } from '../../model/piece-context';
import { useSelectedLayer } from '../../selection/hooks/use-selected-layer';
import { LayerId, SobreimpresoContent } from '../../model/types';
import { SobreimpresoTextbox } from './types';
import {
  setSobreimpresoBorderRadius,
  setSobreimpresoMinHeight,
  setSobreimpresoPaddingX,
  setSobreimpresoPaddingY,
  setSobreimpresoWidth
} from './accessors';
import { getSobreimpresoNaturalHeight } from './render';

type Props = { s: SobreimpresoTextbox };

const inputStyle: React.CSSProperties = { width: 64 };

type Field = {
  tooltip: string;
  value: number;
  apply: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
};

export const SobreimpresoNumberInputs: React.FC<Props> = ({ s }) => {
  const dispatch = usePieceDispatch();
  const layer = useSelectedLayer();

  const [width, setWidth] = useState(0);
  const [minHeight, setMinHeight] = useState(0);
  const [paddingX, setPaddingX] = useState(0);
  const [paddingY, setPaddingY] = useState(0);
  const [borderRadius, setBorderRadius] = useState(0);

  // Rounded — depends on text, paddingY, font, etc. (derived render value).
  const naturalHeight = Math.round(getSobreimpresoNaturalHeight(s));

  // Sync the controls from the model.
  useEffect(() => {
    if (layer?.kind !== 'sobreimpreso') return;
    const c = layer.content;
    setWidth(c.width);
    setMinHeight(c.minHeight);
    setPaddingX(c.paddingX);
    setPaddingY(c.paddingY);
    setBorderRadius(c.borderRadius);
  }, [layer]);

  // Commit uses the object's own id (stable) so clicking out — which deselects and
  // unmounts the panel — doesn't lose the change. onChange records with the fresh value.
  const patch = (p: Partial<SobreimpresoContent>) => {
    const id = s.layerId as LayerId | undefined;
    if (id) dispatch({ type: 'patchLayerContent', id, patch: p });
  };

  const fields: Field[] = [
    {
      tooltip: 'Ancho',
      value: Math.round(width),
      min: 10,
      max: 4000,
      step: 5,
      apply: v => {
        setWidth(v);
        setSobreimpresoWidth(s, v);
        patch({ width: v });
      }
    },
    {
      tooltip: 'Alto',
      value: Math.max(Math.round(minHeight), naturalHeight),
      min: naturalHeight,
      max: 4000,
      step: 10,
      apply: v => {
        setMinHeight(v);
        setSobreimpresoMinHeight(s, v);
        patch({ minHeight: v });
      }
    },
    {
      tooltip: 'Padding horizontal',
      value: paddingX,
      min: 0,
      max: 400,
      apply: v => {
        setPaddingX(v);
        setSobreimpresoPaddingX(s, v);
        patch({ paddingX: v });
      }
    },
    {
      tooltip: 'Padding vertical',
      value: paddingY,
      min: 0,
      max: 400,
      apply: v => {
        setPaddingY(v);
        setSobreimpresoPaddingY(s, v);
        patch({ paddingY: v });
      }
    },
    {
      tooltip: 'Esquinas redondeadas',
      value: borderRadius,
      min: 0,
      max: 400,
      apply: v => {
        setBorderRadius(v);
        setSobreimpresoBorderRadius(s, v);
        patch({ borderRadius: v });
      }
    }
  ];

  return (
    <>
      {fields.map(f => (
        <Tooltip key={f.tooltip} title={f.tooltip}>
          <InputNumber
            size='small'
            style={inputStyle}
            min={f.min}
            max={f.max}
            step={f.step ?? 1}
            precision={0}
            value={f.value}
            onChange={v => v != null && f.apply(v)}
          />
        </Tooltip>
      ))}
    </>
  );
};
